import { Fragment, FunctionalComponent } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import { lazy, Suspense } from 'preact/compat';
import { useDispatch, useSelector } from 'react-redux';

import ErrorBoundary from './ErrorBoundary';
import SearchForm from './features/searchForm';
import TopSites from './features/tiles';
import TotalCounter from './features/totalCounter';
import Logo from './features/logo';
import Banner from './features/banner';

import { selectFeatureFlags, selectUiMode } from './store/selectors';
import { selectMessageShown, fetchMessages } from './features/story/slice';
import { selectTriviaState } from './features/trivia/slice';
import { selectUnreadMessages } from './features/friends/slice/selectors';

import styles from './styles.module.scss';
import useHasBookmarks from './features/user/bookmarksBar/useHasBookmarksBar';

const Trivia = lazy(() => import('./features/trivia'));
const Story = lazy(() => import('./features/story'));
const FriendMessage = lazy(
  () => import('./features/friends/components/Message')
);

interface PageState {
  value?: any;
  type: 'friend' | 'counter' | 'story';
}

const COUNTER: PageState = { value: null, type: 'counter' };

const PageContent: FunctionalComponent = () => {
  const dispatch = useDispatch();
  const storyMessage = useSelector(selectMessageShown);
  const uiMode = useSelector(selectUiMode);
  const featureFlags = useSelector(selectFeatureFlags);

  const [showTrivia, setShowTrivia] = useState(false);
  const triviaState = useSelector(selectTriviaState);
  const hasBookmarks = useHasBookmarks();

  useEffect(() => {
    if (uiMode === 'focus') return;
    setShowTrivia(triviaState.showTrivia);
  }, []);

  const unreadFriendMessages = useSelector(selectUnreadMessages);
  //   TODO
  // .filter(
  //     ({ type }) => type === 'TENOR'
  //   )

  const [message, setMessage] = useState<PageState>(COUNTER);

  const isInitial = useRef(true);
  useEffect(() => {
    dispatch(fetchMessages(!isInitial.current));
    isInitial.current = false;

    if (uiMode === 'focus') {
      setShowTrivia(false);
    }
  }, [uiMode === 'focus', featureFlags.STORY_MESSAGES]);

  useEffect(() => {
    setMessage((): PageState => {
      if (storyMessage?.message) {
        return { value: storyMessage, type: 'story' };
      }

      if (uiMode !== 'focus') {
        if (unreadFriendMessages.length) {
          return { value: unreadFriendMessages[0], type: 'friend' };
        }
      }

      return COUNTER;
    });
  }, [storyMessage, unreadFriendMessages]);

  const handleCloseFriendMessage = () => {
    const index = unreadFriendMessages.findIndex(
      ({ id }) => id === message.value.id
    );
    const next = unreadFriendMessages[index + 1];
    setMessage(next ? { type: 'friend', value: next } : COUNTER);
  };

  const renderCounter = () => (
    <Fragment>
      <Logo />
      <TotalCounter />
    </Fragment>
  );

  // hasBookmarks
  return (
    <div
      className={styles.pageContent}
      style={{ marginTop: hasBookmarks ? '61px' : 'calc(61px + 46px)' }}
    >
      <Banner />

      <ErrorBoundary message={null} name="logo">
        <div className={styles.pageContentHeader}>
          <div>{message.type === 'counter' && renderCounter()}</div>

          {message.type === 'friend' && (
            <Suspense fallback={renderCounter()}>
              <FriendMessage
                message={message.value}
                onClose={handleCloseFriendMessage}
              />
            </Suspense>
          )}

          {message.type === 'story' && (
            <Suspense fallback={renderCounter()}>
              <Story shown={message.value} />
            </Suspense>
          )}
        </div>
      </ErrorBoundary>

      <ErrorBoundary message={null} name="trivia">
        {showTrivia && (
          <Suspense fallback={null}>
            <Trivia />
          </Suspense>
        )}
      </ErrorBoundary>

      <SearchForm />

      <ErrorBoundary message={null} name="topSites">
        <TopSites />
      </ErrorBoundary>
    </div>
  );
};

export default PageContent;
